import { makeAutoObservable } from 'mobx';
import { IAuth } from './authentication/IAuth';
import { SPAuth } from './authentication/SPAuth';
import PersonalSettingsState from './PersonalSettingsState';
import API, {
	EntityTypes,
	ISettings,
	IUserConfigPackage,
} from '../API';
import { TemplatesState } from '../components/pages/templates/Templates';
import { EntityTypesState } from '../components/pages/entity-types/EntityTypes';
import { LogStreamingState } from '../components/pages/logging/LogStreaming';
import { SearchScopesListState } from '../components/pages/search-scopes/SearchScopesList';
import { ManagedPropertiesListState } from '../components/pages/search-properties/ManagedPropertiesList';
import { MSALConfigGen } from '../authConfig';
import { CurrentUserAccountState } from '../components/controls/CurrentUserAccount';
import { Pages } from '../Pages';

export interface IRootStateProps {
	RootState: RootState;
}


export class RootState {
	
	public currentUserAccountState?: CurrentUserAccountState;
	toggleIsNavCollapsed(): void {
		this.isNavCollapsed = !this.isNavCollapsed;
	}
	
	//Section for routing/URL handling
	public hash: string = ''; //The hash contains the arguments
	public page: string = 'entities'; //The page is the first part of the hash
	public argument: string | undefined; //Second part of the hash e.g. project ID
	public argument2: string | undefined; //Third part of the hash e.g. task ID

	//Stuff to do with login
	public name: string = '';

	public spAuth: SPAuth;

	public settingsState?: PersonalSettingsState;
	public userId: string = '';

	public isNavCollapsed:boolean = true;
	public hidePageHeader: boolean = false;

	public showCreateTaskDialog: boolean = false;

	public canNavigateBack: boolean =
		document.referrer.replace(/\/$/, '') === MSALConfigGen.getMsalConfig().auth.redirectUri;

	public auth: IAuth;

	public isUserInfoLoaded: boolean = false;
	private previouslyLoadedPages: string[] = [];

	public userConfig?: IUserConfigPackage;

	public templates?: TemplatesState;
	public entityTypes?: EntityTypesState;
	public searchScopes?:SearchScopesListState;
	public managedProps?:ManagedPropertiesListState;
	public logs?:LogStreamingState;

	constructor(auth: IAuth) {
		makeAutoObservable(this);

		this.auth = auth;
		this.spAuth = new SPAuth(this.auth.getToken, () => {});
	}

	public async initialize(): Promise<void> {
		if (this._initialLoaded) return;

		this._initialLoaded = true;

		if (this.isUserInfoLoading || this.isUserInfoLoaded) {
			return;
		}

		this.isUserInfoLoading = true;

		try {
			await this.loadSettings();
			this.setIsUserInfoLoaded(true);
		} finally {
			this.isUserInfoLoading = false;
		}

		this.setRoute(
			window.location.hash || 'types',
		);
	}

	public useCachedSettings:boolean = false;

	async loadSettings() {
		
		if(this.useCachedSettings) {
			//Load the settings from local storage
			let cachedSettings = localStorage.getItem('userSettings');
			if(cachedSettings) {
				try {
					let parsedSettings:ISettings = JSON.parse(cachedSettings);
					this.setSettings(parsedSettings);
						
					setTimeout(() => {
						//Don't do it immediately to avoid slowing start times
						this.reloadAndStoreSettings();
					}, 5000);

					return;
				} catch {
					//Failed to parse - they'll be reloaded for the next refresh
				}
				
			}
		} 

		let token = await this.auth.getToken();
		let userSettings = await new API(token).getSettings();
		this.setSettings(userSettings);
		localStorage.setItem('userSettings', JSON.stringify(userSettings));
	}
	
	async reloadAndStoreSettings() {

		//Load settings anyway and store them in case there was an update
		let token = await this.auth.getToken();
		var api = new API(token);
		api.getSettings().then((freshSettings) => {
			localStorage.setItem('userSettings', JSON.stringify(freshSettings));
		});

		api.userPing();
	}

	setSettings(userSettings: ISettings) {
		this.userConfig = {
			getToken: this.auth.getToken.bind(this.auth),
			entityTypes: new EntityTypes(userSettings.entityTypes),
			currentUserId: userSettings.currentUserId,
			tenantUrl: userSettings.tenantUrl,
			isAdmin: userSettings.isAdmin,
			tenantId: userSettings.tenantId,
			outlookPreventAttachments: userSettings.outlookPreventAttachments,
		};

		this.currentUserAccountState = new CurrentUserAccountState(this.userConfig);

		this.settingsState = new PersonalSettingsState(this.userConfig, userSettings);
		this.settingsState.setUserId(this.userId); //userId may or may not be available yet

		this.templates = new TemplatesState(this.userConfig);

		this.entityTypes = new EntityTypesState(this.userConfig);
		this.logs = new LogStreamingState(this.userConfig);
		this.searchScopes = new SearchScopesListState(this.userConfig);
		this.managedProps = new ManagedPropertiesListState(this.userConfig);
	}

	public setRoute(hash: string) {
		this.hash = hash.replace('#', '');
		let hashParts = this.hash.split('/');

		this.page = hashParts[0];
		this.argument = decodeURIComponent(hashParts[1] || '');
		this.argument2 = decodeURIComponent(hashParts[2] || '');

		if (!this.hash) {
			//If we're receiving a token from the microsoft page for payments then show the payments page
			let appSrcPaymentToken = RootState.getUrlParameter('token');
			if (appSrcPaymentToken) {
				this.page = Pages.account;
				this.argument = appSrcPaymentToken;
			}
		}

		//The remainder here is only to retrieve data for initial load of the page. After that point, it doesn't have to be loaded again.
		if (this.previouslyLoadedPages.indexOf(this.page) >= 0) return;

		//Store it so we know we've loaded the data.
		this.previouslyLoadedPages = this.previouslyLoadedPages.concat(this.page);
	}

	public toggleCreateTaskDialog(templateId?: string | undefined): void {
		this.showCreateTaskDialog = !this.showCreateTaskDialog;
	}

	static isOAuthRedirect = (): boolean => {
		let hash = (window.location.hash || '').replace('#', '');
		return hash.startsWith('code=') || hash.startsWith('id_token=');
	};

	static getUrlParameter = (sParam: string) => {
		var sPageURL = window.location.search.substring(1),
			sURLVariables = sPageURL.split('&'),
			sParameterName,
			i;

		for (i = 0; i < sURLVariables.length; i++) {
			sParameterName = sURLVariables[i].split('=');

			if (sParameterName[0] === sParam) {
				return sParameterName[1] === undefined
					? ''
					: decodeURIComponent(sParameterName[1]);
			}
		}
		return '';
	};

	private _initialLoaded = false;

	public isUserInfoLoading: boolean = false;

	public setIsUserInfoLoading(isLoading: boolean) {
		this.isUserInfoLoading = isLoading;
	}

	public setIsUserInfoLoaded(isLoaded: boolean) {
		this.isUserInfoLoaded = isLoaded;
	}
}
